import { __awaiter } from "tslib";
import * as React from 'react';
import { useState } from 'react';
import { ChoiceGroup, DefaultButton, Dropdown, MessageBar, MessageBarType, PrimaryButton, Stack, Text, TextField, } from '@fluentui/react';
const emptyForm = {
    StartDateTime: '',
    EndDateTime: '',
    TimeZone: '',
    EventTypeId: undefined,
    ShowAs: undefined,
    Comments: '',
    RecurrencePattern: undefined,
};
/**
 * FM add-event form (F6) — React port of `fmaddevent.html` (`oofForm`). Fields: From/To
 * datetime, TimeZone, Type, Show As, Comments (required for comment-required types), Recurrence.
 * Validates parity of the source's required/date rules, then emits the form via onSubmit.
 * Self-dependent: no FXP shell needed; the actual create call (GRM) is wired upstream later.
 */
export const AddEventForm = ({ masterData, initial, resources, onSubmit, onCancel, }) => {
    var _a, _b, _c;
    const [form, setForm] = useState(Object.assign(Object.assign({}, emptyForm), initial));
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const selectedType = masterData.EventTypes.find((t) => t.EventTypeId === form.EventTypeId);
    const commentsRequired = !!(selectedType === null || selectedType === void 0 ? void 0 : selectedType.IsCommentRequired);
    // Parity of `isShowAsRequired` (type is NOT busy) / `isTechnologyRequired` (IsSpecialtyRequired).
    const showAsRequired = !!selectedType && selectedType.IsBusy === false;
    const technologyRequired = !!(selectedType === null || selectedType === void 0 ? void 0 : selectedType.IsSpecialtyRequired);
    const isRecurring = !!form.RecurrencePattern && form.RecurrencePattern.toLowerCase() !== 'none';
    const set = (patch) => setForm((f) => (Object.assign(Object.assign({}, f), patch)));
    const validate = () => {
        const e = {};
        if (resources && !form.ResourceAlias) {
            e.ResourceAlias = 'Please select a Resource';
        }
        if (!form.StartDateTime) {
            e.StartDateTime = 'Please enter the From Date';
        }
        if (!form.EndDateTime) {
            e.EndDateTime = 'Please enter the To Date';
        }
        if (form.StartDateTime &&
            form.EndDateTime &&
            new Date(form.EndDateTime) < new Date(form.StartDateTime)) {
            e.EndDateTime = 'To date/time cannot be less than From date/time';
        }
        if (!form.TimeZone) {
            e.TimeZone = 'Please select a value for TimeZone';
        }
        if (form.EventTypeId === undefined || form.EventTypeId === null) {
            e.EventTypeId = 'Please select a value for Event Type';
        }
        if (commentsRequired && !(form.Comments && form.Comments.trim())) {
            e.Comments = 'Please enter Comments';
        }
        if (showAsRequired && (!form.ShowAs || form.ShowAs.toLowerCase() === 'select')) {
            e.ShowAs = 'Please select a value for Show As';
        }
        if (technologyRequired && (form.TechnologyId === undefined || form.TechnologyId === null)) {
            e.TechnologyId = 'Please select a value for Technology';
        }
        if (isRecurring) {
            if (form.RecurrencePatternSelection === 'endsafter') {
                if (!form.RecurrenceEndsAfter || form.RecurrenceEndsAfter <= 0) {
                    e.Recurrence = 'Recurrence occurrence should be greater than 0';
                }
            }
            else if (form.RecurrencePatternSelection === 'endsby') {
                if (!form.RecurrenceEndsBy) {
                    e.Recurrence = 'Please select a recurrence end date';
                }
            }
            else {
                e.Recurrence = 'Please choose how the recurrence ends';
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };
    const handleAdd = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!validate()) {
            return;
        }
        setSubmitError(null);
        setSubmitting(true);
        try {
            yield (onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(form));
        }
        catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to add the event.');
        }
        finally {
            setSubmitting(false);
        }
    });
    const resourceOptions = (resources || []).map((r) => ({
        key: r.alias,
        text: r.name,
    }));
    const tzOptions = masterData.TimeZones.map((t) => ({ key: t, text: t }));
    const typeOptions = masterData.EventTypes.map((t) => ({
        key: t.EventTypeId,
        text: t.EventTypeName,
    }));
    const showAsOptions = (masterData.ShowAs || []).map((s) => ({ key: s, text: s }));
    const techOptions = (masterData.PrimaryTechnologies || []).map((t) => ({
        key: t.Id,
        text: t.Name,
    }));
    const recurrenceOptions = (masterData.RecurrenceData || []).map((r) => ({ key: r, text: r }));
    return (React.createElement(Stack, { tokens: { childrenGap: 12 }, styles: { root: { padding: 20, maxWidth: 460 } }, "data-testid": "add-event-form" },
        React.createElement(Text, { variant: "xLarge", as: "h2" }, "Add an Event"),
        submitError ? (React.createElement(MessageBar, { messageBarType: MessageBarType.error, "data-testid": "add-event-error" }, submitError)) : null,
        resources ? (React.createElement(Dropdown, { label: "RESOURCE", required: true, placeholder: "Select", options: resourceOptions, selectedKey: form.ResourceAlias || undefined, errorMessage: errors.ResourceAlias, onChange: (_e, o) => o && set({ ResourceAlias: o.key }) })) : null,
        React.createElement(TextField, { label: "FROM", required: true, type: "datetime-local", value: form.StartDateTime, errorMessage: errors.StartDateTime, onChange: (_e, v) => set({ StartDateTime: v !== null && v !== void 0 ? v : '' }) }),
        React.createElement(TextField, { label: "TO", required: true, type: "datetime-local", value: form.EndDateTime, errorMessage: errors.EndDateTime, onChange: (_e, v) => set({ EndDateTime: v !== null && v !== void 0 ? v : '' }) }),
        React.createElement(Dropdown, { label: "TIMEZONE", required: true, options: tzOptions, selectedKey: form.TimeZone || undefined, errorMessage: errors.TimeZone, onChange: (_e, o) => o && set({ TimeZone: o.key }) }),
        React.createElement(Dropdown, { label: "TYPE", required: true, options: typeOptions, selectedKey: (_a = form.EventTypeId) !== null && _a !== void 0 ? _a : undefined, errorMessage: errors.EventTypeId, onChange: (_e, o) => o && set({ EventTypeId: o.key }) }),
        showAsRequired ? (React.createElement(Dropdown, { label: "SHOW AS", required: true, placeholder: "Select", options: showAsOptions, selectedKey: form.ShowAs || undefined, errorMessage: errors.ShowAs, onChange: (_e, o) => o && set({ ShowAs: o.key }) })) : null,
        technologyRequired ? (React.createElement(Dropdown, { label: "TECHNOLOGY", required: true, placeholder: "Select", options: techOptions, selectedKey: (_b = form.TechnologyId) !== null && _b !== void 0 ? _b : undefined, errorMessage: errors.TechnologyId, onChange: (_e, o) => o && set({ TechnologyId: o.key }) })) : null,
        React.createElement(TextField, { label: "COMMENTS", multiline: true, required: commentsRequired, value: form.Comments, errorMessage: errors.Comments, onChange: (_e, v) => set({ Comments: v !== null && v !== void 0 ? v : '' }) }),
        recurrenceOptions.length > 0 ? (React.createElement(Dropdown, { label: "RECURRENCE", options: recurrenceOptions, selectedKey: form.RecurrencePattern || undefined, onChange: (_e, o) => o && set({ RecurrencePattern: o.key }) })) : null,
        isRecurring ? (React.createElement(Stack, { tokens: { childrenGap: 8 }, styles: { root: { paddingLeft: 8, borderLeft: '2px solid #edebe9' } } },
            React.createElement(ChoiceGroup, { label: "Ends", selectedKey: form.RecurrencePatternSelection, options: [
                    { key: 'endsafter', text: 'Ends after' },
                    { key: 'endsby', text: 'Ends by' },
                ], onChange: (_e, o) => o && set({ RecurrencePatternSelection: o.key }) }),
            form.RecurrencePatternSelection === 'endsafter' ? (React.createElement(TextField, { label: "Occurrences", type: "number", min: 1, value: form.RecurrenceEndsAfter !== undefined ? String(form.RecurrenceEndsAfter) : '', onChange: (_e, v) => set({ RecurrenceEndsAfter: v ? Number(v) : undefined }) })) : null,
            form.RecurrencePatternSelection === 'endsby' ? (React.createElement(TextField, { label: "End date", type: "datetime-local", value: (_c = form.RecurrenceEndsBy) !== null && _c !== void 0 ? _c : '', onChange: (_e, v) => set({ RecurrenceEndsBy: v !== null && v !== void 0 ? v : '' }) })) : null,
            errors.Recurrence ? (React.createElement(Text, { role: "alert", styles: { root: { color: '#a4262c', fontSize: 12 } } }, errors.Recurrence)) : null)) : null,
        React.createElement(Stack, { horizontal: true, tokens: { childrenGap: 8 } },
            React.createElement(PrimaryButton, { text: submitting ? 'Adding…' : 'Add', onClick: () => void handleAdd(), disabled: submitting }),
            React.createElement(DefaultButton, { text: "Cancel", onClick: () => onCancel === null || onCancel === void 0 ? void 0 : onCancel(), disabled: submitting }))));
};
export default AddEventForm;
//# sourceMappingURL=AddEventForm.js.map